/**
 * Settle a GSTR-3B: how much credit pays which head, and what is left for cash.
 * @file src/web/features/admin/components/OffsetLiability.tsx
 *
 * The portal shows an editable grid and so does this, because the allocation
 * is the taxpayer's to make: after IGST credit has paid IGST, whether the rest
 * goes to CGST or SGST is a choice, and it changes which head needs a challan.
 *
 * What is proposed follows section 49(5) and rule 88A. What is SENT is
 * whatever is on this screen when the button is pressed — and it is checked
 * to the rupee first, because GSTN refuses a partial or excess payment
 * outright rather than correcting it.
 */
import { useState } from 'react';
import { AlertTriangle, Check, Loader2, RefreshCw } from 'lucide-react';
import { adminFetch } from '../lib/admin-api';

type Head = 'igst' | 'cgst' | 'sgst' | 'cess';
const HEADS: [string, Head][] = [['IGST', 'igst'], ['CGST', 'cgst'], ['SGST', 'sgst'], ['Cess', 'cess']];
const inr = (n: number) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const r2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

/** The pairs GSTN has a field for. CGST↔SGST is absent by law, not oversight. */
const PAIRS: [Head, Head][] = [
  ['igst', 'igst'], ['igst', 'cgst'], ['igst', 'sgst'],
  ['cgst', 'cgst'], ['cgst', 'igst'],
  ['sgst', 'sgst'], ['sgst', 'igst'],
  ['cess', 'cess'],
];

export function OffsetLiability({ period, entityId, live, onDone }: {
  period: string; entityId: string | null; live: boolean;
  /** Told once the offset lands, for anything that should re-read. */
  onDone?: () => void;
}) {
  const [data, setData] = useState<any>(null);
  const [use, setUse] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<null | 'read' | 'offset'>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const read = async () => {
    setBusy('read'); setErr(null);
    try {
      const res = await adminFetch('/api/gst/gstr3b/payment', { method: 'POST', query: { entityId: entityId ?? undefined, period } });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (out.needsSession) throw new Error('No portal session — open one at the top of this screen first.');
        throw new Error(out.error || `Could not read the liability (HTTP ${res.status})`);
      }
      setData(out);
      const seeded: Record<string, number> = {};
      for (const c of out.proposal?.credit ?? []) seeded[`${c.from}>${c.to}`] = r2(c.amount);
      setUse(seeded);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  };

  const main = data?.lines?.find((l: any) => !l.reverseCharge) ?? data?.lines?.[0] ?? null;
  const creditOf = (h: Head) => Number(data?.credit?.[h] ?? 0);
  const usedFrom = (h: Head) => r2(PAIRS.filter(([f]) => f === h).reduce((t, [f, to]) => t + (use[`${f}>${to}`] || 0), 0));
  const paidTo = (h: Head) => r2(PAIRS.filter(([, to]) => to === h).reduce((t, [f, to]) => t + (use[`${f}>${to}`] || 0), 0));
  const owed = (h: Head) => r2(Number(main?.tax?.[h] ?? 0));
  const cashFor = (h: Head) => r2(Math.max(0, owed(h) - paidTo(h)));

  const overCredit = HEADS.filter(([, h]) => usedFrom(h) > creditOf(h) + 0.004).map(([l]) => l);
  const overPaid = HEADS.filter(([, h]) => paidTo(h) > owed(h) + 0.004).map(([l]) => l);
  /* Rule 88A: CGST or SGST credit may not be used while IGST credit remains. */
  const igstLeft = r2(creditOf('igst') - usedFrom('igst'));
  const breaks88A = igstLeft > 0.004 && (usedFrom('cgst') > 0.004 || usedFrom('sgst') > 0.004);
  const blocked = overCredit.length > 0 || overPaid.length > 0;

  const cashNeeded = r2(HEADS.reduce((t, [, h]) =>
    t + cashFor(h) + Number(main?.interest?.[h] ?? 0) + Number(main?.lateFee?.[h] ?? 0), 0));
  const shortOn = HEADS.filter(([, h]) => {
    const need = r2(cashFor(h) + Number(main?.interest?.[h] ?? 0) + Number(main?.lateFee?.[h] ?? 0));
    return need > Number(data?.balances?.cash?.[h] ?? 0) + 0.004;
  });

  const send = async () => {
    setBusy('offset'); setErr(null);
    try {
      const allocations = (data.lines as any[]).map((line) => {
        const isMain = line === main;
        const credit = isMain
          ? PAIRS.filter(([f, t2]) => (use[`${f}>${t2}`] || 0) > 0).map(([from, to]) => ({ from, to, amount: r2(use[`${from}>${to}`]) }))
          : [];
        const cashTax: any = {}; for (const [, h] of HEADS) cashTax[h] = isMain ? cashFor(h) : r2(Number(line.tax?.[h] ?? 0));
        return { line, credit, cashTax, cashInterest: line.interest, cashLateFee: line.lateFee };
      });
      const res = await adminFetch('/api/gst/gstr3b/offset', {
        method: 'POST', query: { entityId: entityId ?? undefined, period },
        body: JSON.stringify({ allocations, confirmed: true }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || `The portal refused the offset (HTTP ${res.status})`);
      setDone(out.message || 'Payment of tax done.');
      setConfirming(false);
      onDone?.();
    } catch (e) { setErr((e as Error).message); setConfirming(false); } finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-ink-200 bg-white px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink-900">Offset the liability</p>
          <p className="mt-0.5 text-xs text-ink-500">
            Credit first, cash for the rest. Proposed under section 49(5) and rule 88A — change any figure before sending; what is sent is what is shown.
          </p>
        </div>
        <button onClick={read} disabled={!!busy || !live}
          title={live ? undefined : 'Needs an open portal session'}
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-ink-300 bg-white px-4 py-2 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">
          {busy === 'read' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {data ? 'Read again' : 'Read the liability'}
        </button>
      </div>

      {err && <p className="mt-3 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-xs text-danger-800">{err}</p>}
      {done && (
        <p className="mt-3 flex items-center gap-2 rounded-xl border border-ok-200 bg-ok-50 px-3.5 py-2.5 text-sm font-semibold text-ok-800">
          <Check className="h-4 w-4" /> {done} The return can be filed now.
        </p>
      )}

      {!data && !err && <p className="mt-3 text-xs text-ink-500">Not read yet. {live ? 'The liability and the ledgers come from the portal.' : 'Open a portal session first.'}</p>}

      {data && main && !done && (
        <>
          <div className="mt-3 overflow-x-auto rounded-xl border border-ink-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-200 text-left text-[11px] uppercase tracking-wide text-ink-400">
                  <th className="px-3 py-2 font-medium">Credit used</th>
                  {HEADS.map(([l]) => <th key={l} className="px-3 py-2 text-right font-medium">pays {l}</th>)}
                  <th className="px-3 py-2 text-right font-medium">Available</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {HEADS.map(([label, from]) => (
                  <tr key={from}>
                    <td className="px-3 py-1.5 font-medium text-ink-800">{label}</td>
                    {HEADS.map(([, to]) => {
                      const allowed = PAIRS.some(([f, t2]) => f === from && t2 === to);
                      return (
                        <td key={to} className="px-3 py-1.5 text-right">
                          {allowed ? (
                            <input
                              value={use[`${from}>${to}`] ?? 0}
                              onChange={(e) => setUse((u) => ({ ...u, [`${from}>${to}`]: r2(Number(e.target.value.replace(/[^\d.]/g, '')) || 0) }))}
                              className="w-28 rounded-lg border border-ink-200 px-2 py-1 text-right text-xs tabular-nums focus:border-inkblack focus:outline-none"
                            />
                          ) : (
                            /* No field exists for this pair: the law, in the schema. */
                            <span className="text-ink-300">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className={`px-3 py-1.5 text-right tabular-nums ${usedFrom(from) > creditOf(from) + 0.004 ? 'font-semibold text-danger-700' : 'text-ink-600'}`}>
                      {inr(creditOf(from))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-ink-200 text-xs">
                <tr className="bg-ink-50/60">
                  <td className="px-3 py-1.5 font-semibold text-ink-700">Liability</td>
                  {HEADS.map(([l, h]) => <td key={l} className="px-3 py-1.5 text-right tabular-nums font-semibold">{inr(owed(h))}</td>)}
                  <td />
                </tr>
                <tr>
                  <td className="px-3 py-1.5 text-ink-600">Left for cash</td>
                  {HEADS.map(([l, h]) => <td key={l} className="px-3 py-1.5 text-right tabular-nums text-ink-800">{inr(cashFor(h))}</td>)}
                  <td />
                </tr>
                {(Number(main.interest?.igst) || Number(main.lateFee?.cgst)) ? (
                  <tr>
                    <td className="px-3 py-1.5 text-ink-600">Interest &amp; late fee — cash only</td>
                    {HEADS.map(([l, h]) => <td key={l} className="px-3 py-1.5 text-right tabular-nums text-ink-800">{inr(Number(main.interest?.[h] ?? 0) + Number(main.lateFee?.[h] ?? 0))}</td>)}
                    <td />
                  </tr>
                ) : null}
                <tr>
                  <td className="px-3 py-1.5 text-ink-600">Cash in the ledger</td>
                  {HEADS.map(([l, h]) => <td key={l} className="px-3 py-1.5 text-right tabular-nums text-ink-500">{inr(Number(data.balances?.cash?.[h] ?? 0))}</td>)}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {breaks88A && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-warn-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              ₹{inr(igstLeft)} of IGST credit is still unused. Rule 88A requires IGST credit to be exhausted before CGST or SGST credit is touched — GSTN will refuse this.
            </p>
          )}
          {overCredit.length > 0 && (
            <p className="mt-2 text-xs font-semibold text-danger-700">More {overCredit.join(' and ')} credit is being used than the ledger holds.</p>
          )}
          {overPaid.length > 0 && (
            <p className="mt-2 text-xs font-semibold text-danger-700">More is allocated to {overPaid.join(' and ')} than is owed. GSTN refuses an excess payment.</p>
          )}
          {shortOn.length > 0 && (
            <p className="mt-2 text-xs text-danger-700">
              Cash is short on {shortOn.map(([l]) => l).join(', ')} — <span className="font-semibold">₹{inr(cashNeeded)}</span> is needed in total.
              A challan has to be paid and credited before this will go through.
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {!confirming ? (
              <button onClick={() => setConfirming(true)} disabled={!!busy || blocked}
                className="inline-flex items-center gap-2 rounded-full bg-danger-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40">
                Offset ₹{inr(cashNeeded)} from cash and the credit above
              </button>
            ) : (
              <>
                <span className="text-xs text-ink-700">This debits the ledgers and cannot be undone. Go ahead?</span>
                <button onClick={send} disabled={busy === 'offset'}
                  className="inline-flex items-center gap-2 rounded-full bg-danger-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40">
                  {busy === 'offset' && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Yes, offset it
                </button>
                <button onClick={() => setConfirming(false)} disabled={busy === 'offset'}
                  className="rounded-full border border-ink-300 bg-white px-4 py-2 text-xs font-semibold text-ink-700">Cancel</button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
